import { Stack } from '@mui/material';
import {
  useGetGuestJourneyRetentionCardsQuery,
  useGetGuestJourneyRetentionConversionTrendsQuery,
} from 'api/reports-service/guest-journey-hooks';
import SearchToolbar from 'components/search/search-toolbar/search-toolbar.component';
import {
  MultipleSearchQueryProvider,
  SearchParamsProvider,
} from 'components/search/search.context';
import {
  useSearchParam,
  useSearchParams,
  useSearchQuery,
} from 'components/search/search.hooks';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import DataPlusGate from '../data-plus/data-plus-gate.component';
import {
  retentionFilteredSearchParams,
  useRetentionSearchParams,
} from '../guest-journey.hooks';
import ReportFieldsLink from '../report-fields/report-fields-link.component';
import RetentionConversion from './retention-conversion.component';
import RetentionDetailTable from './retention-detail-table.component';
import RetentionLockedPreview from './retention-locked-preview.component';
import RetentionLtvCards from './retention-ltv-cards.component';
import {
  criticalVisitNumbers,
  withConversionFromSignup,
} from './retention.utils';

/**
 * Queries only send the params on this tab's filter bar (Locations + Sign
 * Up Date Range + Source + Compare).
 */
const filteredSearchParams = retentionFilteredSearchParams;

const SearchParamsWrapper = () => {
  const { t } = useTranslation();
  const searchParams = useSearchParams(useRetentionSearchParams());
  return (
    <SearchParamsProvider value={searchParams}>
      <DataPlusGate
        featureName={t('page.guestJourney.tabs.retention.label')}
        preview={<RetentionLockedPreview />}
      >
        <Retention />
      </DataPlusGate>
    </SearchParamsProvider>
  );
};

const Retention = () => {
  const cards = useSearchQuery(useGetGuestJourneyRetentionCardsQuery, {
    filteredSearchParams,
  });
  const conversionTrends = useSearchQuery(
    useGetGuestJourneyRetentionConversionTrendsQuery,
    { filteredSearchParams },
  );

  const signUpStart = useSearchParam<DateTime>('startdate')?.value;
  const signUpEnd = useSearchParam<DateTime>('enddate')?.value;
  const signUpRangeDays =
    signUpStart && signUpEnd
      ? Math.max(0, Math.round(signUpEnd.diff(signUpStart, 'days').days))
      : undefined;

  const visits = withConversionFromSignup(conversionTrends.data);
  const criticalVisits = criticalVisitNumbers(conversionTrends.data);

  return (
    <Stack marginTop={2} data-testid="GuestJourney.Retention">
      <MultipleSearchQueryProvider
        value={{ queries: { cards, conversionTrends } }}
      >
        <Stack spacing={2}>
          <SearchToolbar />

          <ReportFieldsLink tab="retention" />

          <RetentionLtvCards
            cards={cards.data}
            signUpRangeDays={signUpRangeDays}
            loading={cards.queryState.isFetching}
          />

          <RetentionConversion
            visits={visits}
            criticalVisits={criticalVisits}
          />

          <RetentionDetailTable visits={visits} />
        </Stack>
      </MultipleSearchQueryProvider>
    </Stack>
  );
};

export default SearchParamsWrapper;
